import { Injectable, inject, signal } from '@angular/core';
import { IS_PROD } from '../env';
import { IS_NATIVE } from '../env';
import { APP_VERSION } from '../version';
import { sendMixpanel } from '../services/mixpanel.sink';
import { AuthService } from '../services/auth.service';
import { getAnalytics, isSupported, logEvent, setAnalyticsCollectionEnabled, setUserId, type Analytics } from 'firebase/analytics';

/** Mixpanel project token (public): EU project 4061484. */
const MIXPANEL_TOKEN = '68b0aa4183b9e7c7e3534274b1f488de';

/** Present = the user switched usage sharing off (Profile). Absent = on. */
const OPT_OUT_KEY = 'av.analytics.optout';
function readOptOut(): boolean {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Product analytics — two sinks from one call site: Firebase Analytics (GA4)
 * and Mixpanel (EU). No advertising cookies or cross-site trackers. Fire-and-forget: analytics must never break or slow a feature.
 *
 * Privacy rule of thumb: record THAT something happened, not the personal
 * payload — no registrations, emails or free text in props. That rule is what
 * makes it safe to mirror the same events into Firebase Analytics (GA4),
 * which is what gives us funnels, retention and the engagement numbers the
 * app stores care about.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly auth = inject(AuthService);
  private sessionStarted = false;
  /** "Help improve Vekkle" — default on; off silences GA4 AND Mixpanel. */
  readonly enabled = signal(!readOptOut());

  setEnabled(on: boolean): void {
    this.enabled.set(on);
    try {
      if (on) localStorage.removeItem(OPT_OUT_KEY);
      else localStorage.setItem(OPT_OUT_KEY, '1');
    } catch {
      /* storage blocked — in-memory state still applies for this session */
    }
    void this.gaReady()
      .then((ga) => ga && setAnalyticsCollectionEnabled(ga, on))
      .catch(() => undefined);
  }
  /** Resolves once; null where GA can't run (unsupported env, blocked). */
  private ga: Promise<Analytics | null> | null = null;

  private gaReady(): Promise<Analytics | null> {
    if (!this.ga) {
      this.ga = isSupported()
        .then((ok) => {
          if (!ok) return null;
          const ga = getAnalytics(this.auth.app);
          if (!this.enabled()) setAnalyticsCollectionEnabled(ga, false);
          return ga;
        })
        .catch(() => null);
    }
    return this.ga;
  }

  private readonly lastFired = new Map<string, number>();

  track(name: string, props: Record<string, string | number | boolean> = {}): void {
    this.lastFired.set(name, Date.now());
    void this.write(name, props);
  }

  /** When an event last fired this session (ms epoch), or 0 — lets callers pair a view with what followed. */
  lastAt(name: string): number {
    return this.lastFired.get(name) ?? 0;
  }

  /** Once per app load. */
  appOpen(): void {
    if (this.sessionStarted) return;
    this.sessionStarted = true;
    this.track('app_open');
  }

  private async write(name: string, props: Record<string, string | number | boolean>): Promise<void> {
    try {
      if (!this.enabled()) return;
      await this.auth.ready;
      const uid = this.auth.uid();
      if (!uid) return;
      // GA4 mirror — fire and forget, never blocks or breaks the Firestore write.
      void this.gaReady()
        .then((ga) => {
          if (!ga) return;
          setUserId(ga, uid);
          logEvent(ga, name.slice(0, 40), props);
        })
        .catch(() => undefined);
      // Mixpanel (funnels, retention, per-user timelines) — same opt-out, same "no personal payload" rule.
      sendMixpanel(MIXPANEL_TOKEN, name.slice(0, 40), uid, { ...props, app: 'car', env: IS_PROD ? 'prod' : 'dev', platform: IS_NATIVE ? 'ios' : 'web', app_version: APP_VERSION });
    } catch {
      /* analytics never throws */
    }
  }
}
